import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import {
  ExceptionWithMessage,
  UploadValidationException,
} from 'shared/helpers/CustomErrors';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns: _state => campaignType => {
    return _state.records.filter(
      record => record.campaign_type === campaignType
    );
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
};

export const actions = {
  get: async function getCampaigns({ commit }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get();
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  createWhatsapp: async function createWhatsappCampaign(
    { commit },
    campaignObj
  ) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      let payload;
      if (campaignObj.contacts_csv && campaignObj.contacts_csv.length !== 0) {
        payload = new FormData();

        payload.append('campaign[title]', campaignObj.title);
        payload.append('campaign[message]', campaignObj.message);
        payload.append('campaign[inbox_id]', campaignObj.inbox_id);
        payload.append('campaign[scheduled_at]', campaignObj.scheduled_at);
        payload.append(
          'campaign[messages_per_minute]',
          campaignObj.messages_per_minute
        );

        payload.append(
          'campaign[audience]',
          JSON.stringify(campaignObj.audience)
        );

        payload.append(
          'campaign[template_info]',
          JSON.stringify(campaignObj.template_info)
        );

        payload.append(
          'campaign[assignee_team_ids]',
          JSON.stringify(campaignObj.assignee_team_ids || [])
        );

        payload.append(
          'campaign[assignee_agent_ids]',
          JSON.stringify(campaignObj.assignee_agent_ids || [])
        );

        payload.append(
          'campaign[hide_from_agents]',
          campaignObj.hide_from_agents
        );

        payload.append(
          'campaign[should_resolve]',
          campaignObj.should_resolve
        );

        payload.append('contacts_csv', campaignObj.contacts_csv);
      } else {
        payload = campaignObj;
      }
      const response = await CampaignsAPI.createWhatsappCampaign(payload);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      if (error.response?.data?.error === 'validation_failed') {
        throw new UploadValidationException(error.response.data);
      }
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      }
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
